/**
 * CALCULADORA 03 — ASME B31.3
 * Minimum required wall thickness of straight pipe under internal pressure.
 *
 * Independent calculator: it produces a thickness, never a corrosion rate or
 * a remaining life. Its result may be handed to the API 570 calculator as the
 * minimum required thickness, but only through an explicit action.
 */

export interface AsmeB313Inputs {
  /** P — internal design pressure. */
  designPressure: number | null;
  /** D — outside diameter. */
  outsideDiameter: number | null;
  /** S — allowable stress, in the same pressure unit as P. */
  allowableStress: number | null;
  /** E — quality factor. */
  qualityFactor: number | null;
  /** W — weld joint strength reduction factor. */
  weldJointFactor: number | null;
  /** Y — coefficient from Table 304.1.1. */
  yCoefficient: number | null;
  /** CA — corrosion allowance, same length unit as D. */
  corrosionAllowance: number | null;
  /** EA — erosion allowance. */
  erosionAllowance: number | null;
  /** MA — mechanical allowance. */
  mechanicalAllowance: number | null;
  /** Negative manufacturing tolerance, as a percentage (e.g. 12.5). */
  negativeTolerancePercent: number | null;
  /** Recorded with the result; not part of the formula. */
  material?: string;
  designTemperatureCelsius?: number | null;
  pressureUnit?: string;
  lengthUnit?: string;
}

export interface AsmeB313Result {
  /** t — thickness required by pressure alone. */
  pressureThickness: number | null;
  corrosionAllowance: number;
  erosionAllowance: number;
  mechanicalAllowance: number;
  /** c — total allowance. */
  totalAllowance: number;
  /** tm = t + c */
  minimumRequiredThickness: number | null;
  negativeTolerancePercent: number;
  /** tm / (1 − tolerance) */
  requiredNominalThickness: number | null;
  warnings: string[];
}

const num = (value: number | null | undefined): number =>
  value !== null && value !== undefined && Number.isFinite(value) ? value : 0;

/**
 * t  = (P × D) / (2 × (S × E × W + P × Y))
 * c  = CA + EA + MA
 * tm = t + c
 * t_nominal = tm / (1 − tolerance)
 */
export function calculateAsmeB313(inputs: AsmeB313Inputs): AsmeB313Result {
  const warnings: string[] = [];

  const P = inputs.designPressure;
  const D = inputs.outsideDiameter;
  const S = inputs.allowableStress;
  const E = inputs.qualityFactor;
  const W = inputs.weldJointFactor;
  const Y = inputs.yCoefficient;

  const corrosion = num(inputs.corrosionAllowance);
  const erosion = num(inputs.erosionAllowance);
  const mechanical = num(inputs.mechanicalAllowance);
  const totalAllowance = corrosion + erosion + mechanical;

  const missing: string[] = [];
  if (P === null || !Number.isFinite(P)) missing.push("Pressão de Projeto (P)");
  if (D === null || !Number.isFinite(D)) missing.push("Diâmetro Externo (D)");
  if (S === null || !Number.isFinite(S)) missing.push("Tensão Admissível (S)");
  if (E === null || !Number.isFinite(E)) missing.push("Fator de Qualidade (E)");
  if (W === null || !Number.isFinite(W)) missing.push("Fator da Junta Soldada (W)");
  if (Y === null || !Number.isFinite(Y)) missing.push("Coeficiente Y");

  let pressureThickness: number | null = null;

  if (missing.length > 0) {
    warnings.push(`Espessura por pressão não calculada: informe ${missing.join(", ")}.`);
  } else {
    const denominator = 2 * (S! * E! * W! + P! * Y!);
    if (denominator === 0) {
      warnings.push("Espessura por pressão não calculada: o denominador da fórmula resultou em zero (verifique S, E, W, P e Y).");
    } else {
      pressureThickness = (P! * D!) / denominator;
    }
  }

  const minimumRequired = pressureThickness === null ? null : pressureThickness + totalAllowance;

  const tolerancePercent = num(inputs.negativeTolerancePercent);
  let requiredNominal: number | null = null;

  if (minimumRequired !== null) {
    if (tolerancePercent <= 0) {
      requiredNominal = minimumRequired;
    } else if (tolerancePercent >= 100) {
      warnings.push("Tolerância negativa inválida: deve ser menor que 100%.");
    } else {
      requiredNominal = minimumRequired / (1 - tolerancePercent / 100);
    }
  }

  return {
    pressureThickness,
    corrosionAllowance: corrosion,
    erosionAllowance: erosion,
    mechanicalAllowance: mechanical,
    totalAllowance,
    minimumRequiredThickness: minimumRequired,
    negativeTolerancePercent: tolerancePercent,
    requiredNominalThickness: requiredNominal,
    warnings
  };
}
